import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, ArrowLeft } from 'lucide-react';
import { Markdown } from './Markdown';
import { LEGAL_DOCS, LegalDocId } from '../../content/legal';

/**
 * Public legal page (Terms / Privacy / Disclaimer). Renders a Markdown document in
 * a readable text column, styled to match the always-dark landing page
 * (#0c0e12 / cream / bronze, Inter). Like the landing page it pins <html> to dark
 * while mounted so the page is consistent regardless of the in-app theme.
 */
export function LegalPage({ doc }: { doc: LegalDocId }) {
  const { title, content } = LEGAL_DOCS[doc];

  useEffect(() => {
    const html = document.documentElement;
    const wasLight = html.classList.contains('light');
    if (wasLight) { html.classList.remove('light'); html.classList.add('dark'); }
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    const prevTitle = document.title;
    document.title = `${title} · MyCryptoPlanner`;
    return () => {
      if (wasLight) { html.classList.remove('dark'); html.classList.add('light'); }
      document.title = prevTitle;
    };
  }, [title]);

  return (
    <div
      className="min-h-screen font-sans antialiased flex flex-col"
      style={{
        backgroundColor: '#0c0e12',
        color: '#f4f1ea',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      {/* Header */}
      <header
        className="sticky top-0 z-50 backdrop-blur-md"
        style={{
          backgroundColor: 'rgba(12,14,18,0.85)',
          borderBottom: '0.5px solid rgba(255,255,255,0.06)',
          padding: '16px 24px',
        }}
      >
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" style={{ color: '#f4f1ea' }} />
            <span className="text-[15px] font-semibold tracking-tight" style={{ color: '#f4f1ea' }}>
              MyCryptoPlanner
            </span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-[13px] transition-opacity hover:opacity-100"
            style={{ color: 'rgba(244,241,234,0.6)' }}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to home
          </Link>
        </div>
      </header>

      {/* Content column */}
      <main className="flex-1 w-full px-6 py-12 sm:py-16">
        <article className="max-w-3xl mx-auto">
          <Markdown content={content} />
        </article>
      </main>

      {/* Footer */}
      <footer
        className="px-6"
        style={{ borderTop: '0.5px solid rgba(255,255,255,0.06)', padding: '28px 24px' }}
      >
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-[11px]" style={{ color: 'rgba(244,241,234,0.4)' }}>
            © 2026 MyCryptoPlanner · Built in Australia 🇦🇺
          </span>
          <div className="flex items-center gap-5">
            <Link to="/terms" className="text-[11px] transition-opacity hover:opacity-80" style={{ color: 'rgba(244,241,234,0.4)' }}>Terms</Link>
            <Link to="/privacy" className="text-[11px] transition-opacity hover:opacity-80" style={{ color: 'rgba(244,241,234,0.4)' }}>Privacy</Link>
            <Link to="/disclaimer" className="text-[11px] transition-opacity hover:opacity-80" style={{ color: 'rgba(244,241,234,0.4)' }}>Disclaimer</Link>
            <a href="mailto:mycryptoplanner@outlook.com" className="text-[11px] transition-opacity hover:opacity-80" style={{ color: 'rgba(244,241,234,0.4)' }}>Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
